import range from 'lodash.range';
import React, { FC } from 'react';
import { useInView } from 'react-intersection-observer';
import { FormattedMessage, useIntl } from 'react-intl';
import styled from 'styled-components';
import activitySearchParams from '../constants/activitySearchParams';
import { useTenantContext } from '../context/TenantContext';
import { LatLon } from '../generated/graphql';
import useEateriesQuery from '../hooks/useEateriesQuery';
import useQueryParams from '../hooks/useQueryParams';
import getRelevantTime from '../utils/getRelevantTime';
import ContentWrapper from './ContentWrapper';
import Carousel, { CarouselChild } from './designsystem/Carousel';
import CompanyTile from './designsystem/CompanyTile';
import GradientBackground from './designsystem/GradientBackground';
import Section from './designsystem/Section';
import { useTileImageSizes } from './designsystem/Tile';
import FavoriteIconButton from './FavoriteIconButton';
import useGetPageSlugByLocale from '../hooks/useGetPageSlugByLocale';

const LIMIT = 12;

interface Props {
    coordinates?: LatLon | null;
}

const ActivityEateries: FC<React.PropsWithChildren<Props>> = ({ coordinates }) => {
    const { getPageSlugByLocale } = useGetPageSlugByLocale();

    const companyPageSlug = getPageSlugByLocale('COMPANY');
    const [{ startDate, endDate, placeId }] = useQueryParams(activitySearchParams);
    const { brandConfig } = useTenantContext();
    // we basically use a month search whenever a day isn't specifically selected
    const dateVars = {
        startDate,
        endDate,
    };
    const [ref, inView] = useInView({ triggerOnce: true, rootMargin: '500px' });
    const { data, loading } = useEateriesQuery({
        skip: !inView,
        variables: {
            limit: LIMIT,
            sortByDistanceTo: coordinates
                ? {
                      lat: coordinates.lat,
                      lon: coordinates.lon,
                  }
                : undefined,
            params: {
                ...dateVars,
                placeId,
            },
        },
    });

    const intl = useIntl();
    const sizes = useTileImageSizes();
    const showEateries = coordinates && data?.companies.hits && data.companies.hits.length > 0;

    return (
        <Section name="eateries" ref={ref as (el: null) => void}>
            {showEateries && (
                <>
                    <GradientBackground />
                    <ContentWrapper>
                        <StyledCarousel
                            title={<FormattedMessage defaultMessage="Eten en drinken in de buurt" />}
                            continuous
                        >
                            {loading &&
                                range(4).map(i => (
                                    <CarouselChild key={i}>
                                        <CompanyTile loading />
                                    </CarouselChild>
                                ))}
                            {!loading &&
                                data?.companies.hits?.map(hit => (
                                    <CarouselChild key={hit.id}>
                                        <CompanyTile
                                            icon={
                                                brandConfig.enableFavorites && (
                                                    <FavoriteIconButton type="company" id={hit.id} />
                                                )
                                            }
                                            videoUrl={hit.videoUrl}
                                            name={hit.name}
                                            place={hit.place}
                                            time={
                                                startDate &&
                                                getRelevantTime(
                                                    intl,
                                                    hit.normalOperatingHours,
                                                    hit.specialOperatingHours
                                                )
                                            }
                                            linkProps={{
                                                href: {
                                                    pathname: `/${companyPageSlug}/${hit.slug}`,
                                                },
                                                locale: intl.locale,
                                            }}
                                            sources={[
                                                {
                                                    srcSet:
                                                        hit.webpListImage?.transform?.srcSet ??
                                                        hit.webpMainImage?.transform?.srcSet,
                                                    type: 'image/webp',
                                                    sizes,
                                                },
                                                {
                                                    srcSet:
                                                        hit.listImage?.transform?.srcSet ??
                                                        hit.mainImage?.transform?.srcSet,
                                                    type: 'image/jpeg',
                                                    sizes,
                                                },
                                            ]}
                                            fallback={{
                                                src: hit.listImage?.transform?.src ?? hit.mainImage?.transform?.src,
                                            }}
                                        />
                                    </CarouselChild>
                                ))}
                        </StyledCarousel>
                    </ContentWrapper>
                </>
            )}
        </Section>
    );
};

export default ActivityEateries;

const StyledCarousel = styled(Carousel)`
    padding: var(--sectionPadding) 0;
`;
